import React from 'react'
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { EnterAmountContentProps } from '../types'
import { TextFieldVariant } from '../../../Interfaces/FieldProps'
import getLocaleCurrencyForAmount from '../../../../utils/getLocaleCurrencyForAmount'
import AdjacentLabels from '../../../Labels/AdjacentLabels'
import { AmountSelection } from '../../utils/paymentModalSteps'

const LabelText = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1">{children}</Typography>
)

const EnterAmountContent: React.FC<EnterAmountContentProps> = ({
  amountOption,
  setAmountOption,
  amountOfPayment,
  isAmountOfPaymentValid,
  handleAmountOfPaymentChange,
  handleKeyDown,
  handleAmountInputBlur,
  amountDue,
  defaultCurrencyCode,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()

  return (
    <FormControl>
      <RadioGroup
        aria-label="amount selection"
        name="amount selection"
        value={amountOption}
        defaultValue={amountOption}
        onChange={(e) => setAmountOption(e.target.value as AmountSelection)}
      >
        <FormControlLabel
          key={AmountSelection.TotalAmountDue}
          value={AmountSelection.TotalAmountDue}
          control={
            <Radio
              sx={{
                color: theme.palette.textOrIcon.checkbox,
              }}
            />
          }
          label={
            <AdjacentLabels
              leftLabel={t(
                'PaymentModal.Option.TotalAmountDue',
                'Pay the total amount due'
              )}
              rightLabel={`${getLocaleCurrencyForAmount(
                amountDue.amount,
                defaultCurrencyCode
              )} ${t('PaymentModal.TaxExcludedNote', 'Does not include tax')}`}
            />
          }
        />
        <FormControlLabel
          key={AmountSelection.DifferentAmount}
          value={AmountSelection.DifferentAmount}
          control={
            <Radio
              sx={{
                color: theme.palette.textOrIcon.checkbox,
              }}
            />
          }
          label={
            <LabelText>
              {t(
                'PaymentModal.Option.DifferentAmount',
                'Enter a different amount (Maximum $10,000). Does not include tax'
              )}
            </LabelText>
          }
        />
      </RadioGroup>
      {amountOption === AmountSelection.DifferentAmount && (
        <TextField
          id="amountOfPayment"
          name="amountOfPayment"
          variant={TextFieldVariant.Filled}
          value={amountOfPayment}
          label={t('PaymentModal.Field.AmountOfPayment', 'Amount of Payment')}
          error={
            !isAmountOfPaymentValid.input || !isAmountOfPaymentValid.beforeMax
          }
          onChange={handleAmountOfPaymentChange}
          onKeyDown={handleKeyDown}
          onBlur={handleAmountInputBlur}
          helperText={
            !isAmountOfPaymentValid.input
              ? t(
                  'PaymentModal.ValidationMessage.GreaterThanZero',
                  'Amount of Payment must be greater than 0.'
                )
              : !isAmountOfPaymentValid.beforeMax
              ? t(
                  'PaymentModal.ValidationMessage.LessThanOrEqualToTotal',
                  'Amount of Payment must be less than or equal to the total.'
                )
              : ''
          }
        />
      )}
    </FormControl>
  )
}

export default EnterAmountContent
